// import { Attachment, FileGenerationStatus, Message, ReplyMessageData } from '../types/chat';
import { Attachment, FileGenerationStatus, Message } from '../types/chat';
import { v4 as uuidv4 } from 'uuid';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import React, { useEffect, useRef, useState, memo } from 'react';
import { ArrowDown, Copy, FileText, Loader2, Pencil, Send, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import logo from '../../../assets/images/bebrave-logo.png';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { modelSupportedExtensionsMap, ModelTypes } from '../types/model.types';
import { ImagePreview } from './ImagePreview';
import { useToast } from '../../../hooks/use-toast';
import ExpandableThinkContainer from './ExpandableThinkContainer';
import { useTranslation } from 'react-i18next';
import { Feedback } from './Feedback';
import { useTheme } from '../../../components/core/theme-provider';
import { ChangeMessage } from './ChangeMessage';
import { MessageLoading } from './MessageLoading';
import { AssistantAttachmentPreview } from './AssistantAttachmentPreview';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import SourceFile from './SourceFile';
import { isValidUrl, placeHrefInContent } from 'utils/chat-utilities';
import { SearchSources } from './SearchSources';
import SearchRelatedList from './SearchRelatedList';
import SearchRelatedImageList from './SearchRelatedImageList';
import SearchRelatedVideoList from './SearchRelatedVideoList';
import { AnalyzingIndicator } from './AnalyzingIndicator';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';
import { escapeMarkdownNumbers } from '../../../utils/chat-utilities';

import { useConfig } from '../../../hooks/use-config';
import { updateMessage } from '../../../services/sse-service';
import { useModelStore } from 'state/store/chat/use-model-store';

const markdownComponents = {
  h1: ({ children }: any) => <h1 className="text-[32px] font-bold mb-2 last:mb-0">{children}</h1>,
  h2: ({ children }: any) => (
    <h2 className="text-[24px] font-semibold mb-2 last:mb-0">{children}</h2>
  ),
  h3: ({ children }: any) => (
    <h3 className="text-[18.72px] font-semibold mb-2 last:mb-0">{children}</h3>
  ),
  h4: ({ children }: any) => <h4 className="text-[16px] font-medium mb-2 last:mb-0">{children}</h4>,
  h5: ({ children }: any) => (
    <h5 className="text-[13.28px] font-medium mb-2 last:mb-0">{children}</h5>
  ),
  h6: ({ children }: any) => (
    <h6 className="text-[10.72px] font-medium mb-2 last:mb-0">{children}</h6>
  ),
  p: ({ children }: any) => <p className="mb-2 last:mb-0 break-words">{children}</p>,
  ul: ({ children }: any) => <ul className="list-disc pl-4 mb-2">{children}</ul>,
  ol: ({ children }: any) => <ol className="list-decimal pl-4 mb-2">{children}</ol>,
  li: ({ children }: any) => <li className="mb-0.5">{children}</li>,
  code: ({ inline, className, children, ...props }: any) => {
    const match = /language-(\w+)/.exec(className || '');
    if (!inline && match) {
      return (
        <SyntaxHighlighter
          style={dracula}
          language={match[1]}
          PreTag="pre"
          customStyle={{
            backgroundColor: '#282C34',
            padding: '1rem',
            borderRadius: '0.375rem',
            overflowX: 'auto',
            fontSize: '0.875rem',
          }}
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      );
    }
    return (
      <code className="bg-gray-700 text-gray-100 rounded px-1 py-0.5 font-mono text-sm" {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children }: any) => (
    <pre className="w-full bg-gray-800 text-gray-100 rounded p-2 overflow-x-auto mb-2">
      {children}
    </pre>
  ),
  blockquote: ({ children }: any) => (
    <blockquote className="border-l-4 border-gray-600 pl-4 italic mb-2">{children}</blockquote>
  ),
  table: ({ children }: any) => (
    <div className="mb-2 overflow-x-auto">
      <Table>{children}</Table>
    </div>
  ),
  thead: ({ children }: any) => <TableHeader>{children}</TableHeader>,
  tbody: ({ children }: any) => <TableBody>{children}</TableBody>,
  tr: ({ children }: any) => <TableRow>{children}</TableRow>,
  th: ({ children }: any) => <TableHead>{children}</TableHead>,
  td: ({ children }: any) => <TableCell>{children}</TableCell>,
  a: ({ href, children }: any) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-gray-100 rounded px-1 py-0.5 text-xs ml-1 text-gray-500 hover:text-gray-700"
    >
      {children}
    </a>
  ),
};

interface StreamingContentProps {
  content: string;
  isUserMessage: boolean;
}

const StreamingContent = memo(({ content, isUserMessage }: StreamingContentProps) => {
  const processedContent = isUserMessage ? escapeMarkdownNumbers(content) : content;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={markdownComponents}
    >
      {processedContent}
    </ReactMarkdown>
  );
});

StreamingContent.displayName = 'StreamingContent';

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
  streamingMessage: Message | undefined;
  isStillGenerating: boolean;
  chatInputHeight?: number;
  onSendMessage?: (content: string, files: Attachment[]) => void;
  currentUser?: { profileImageUrl?: string; itemId?: string } | null;

}

export const ChatMessages = ({
  messages,
  isLoading,
  streamingMessage,
  isStillGenerating,
  chatInputHeight = 186,
  onSendMessage,
}: ChatMessagesProps) => {
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<string>('');

  // console.log('isStillGenerating', isStillGenerating)


  // const setCommentMessage = useChatStore((state) => state.setCommentMessage);
  // const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  // const setReplyMessage = useChatStore((state) => state.setReplyMessage);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const scrollToBottomButton = useRef<HTMLButtonElement>(null);
  // const { pathname } = useLocation();
  const { toast } = useToast();
  const { theme } = useTheme();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    scrollToBottomButton.current?.style.setProperty('display', 'none');
  };

  useEffect(() => {
    scrollToBottom();
  }, [isStillGenerating && messages[messages.length - 1].recordId === streamingMessage?.recordId]);

  useEffect(() => {
    if (messages.length > 0) {
      if (messageContainerRef.current) {
        const { scrollTop, clientHeight, scrollHeight } = messageContainerRef.current;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

        if (isNearBottom) {
          scrollToBottom();
        }
      } else {
        scrollToBottom();
      }
    }
  }, [messages.length, messages[messages.length - 1]?.recordId]);

  useEffect(() => {
    const handleScroll = () => {
      if (messageContainerRef.current) {
        const { scrollTop, clientHeight, scrollHeight } = messageContainerRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 10) {
          scrollToBottomButton.current?.style.setProperty('display', 'none');
        } else {
          scrollToBottomButton.current?.style.setProperty('display', 'flex');
        }
      }
    };

    const container = messageContainerRef.current;
    container?.addEventListener('scroll', handleScroll);

    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const fallbackCopyToClipboard = (text: string) => {
    const tempTextArea = document.createElement('textarea');
    tempTextArea.value = text;
    document.body.appendChild(tempTextArea);
    tempTextArea.select();

    try {
      document.execCommand('copy');
      console.log('Text copied to clipboard successfully!');
    } catch (err) {
      console.error('Failed to copy using execCommand: ', err);
    } finally {
      document.body.removeChild(tempTextArea);
    }
  };

  const handleCopyToClipboard = (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          console.log('Text copied to clipboard successfully!');
        });
      } else {
        fallbackCopyToClipboard(text);
      }
      toast({
        variant: 'success',
        title: t('COPIED'),
        description: t('TEXT_COPY_SUCCESS'),
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_COPY_TEXT'),
        description: t('FAILED_COPY_TO_CLIPBOARD'),
      });
    }
  };

  const copyImageToClipboard = async (fileId: string) => {
    try {
      const itemIdFilePathMap = useChatStore.getState().itemIdFilePathMap;
      const imageSrc = itemIdFilePathMap.get(fileId);

      if (!imageSrc) {
        toast({
          variant: 'destructive',
          title: t('FAILED_TO_COPY_IMAGE'),
          description: t('FAILED_COPY_IMAGE_TO_CLIPBOARD'),
        });
        return;
      }

      try {
        const response = await fetch(imageSrc);
        if (!response.ok) {
          trackEvent(
            EVENTS.ERROR(new Error('Failed to copy image'), {
              endpoint: imageSrc,
              method: 'GET',
              status: response.status,
            })
          );
        }
        const blob = await response.blob();
        const imageBlob = new Blob([blob], { type: 'image/png' });

        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': imageBlob,
          }),
        ]);

        toast({
          variant: 'success',
          title: t('COPIED'),
          description: t('IMAGE_COPY_SUCCESS'),
        });
        return;
      } catch (clipboardError) {
        console.warn('Clipboard API failed, falling back to download:', clipboardError);
      }
    } catch (error) {
      console.error('Failed to copy/download image:', error);
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_COPY_IMAGE'),
        description: t('FAILED_COPY_IMAGE_TO_CLIPBOARD'),
      });
    }
  };

  const handleEditMessage = (messageId: string, content: string) => {
    setEditingMessageId(messageId);
    setEditedContent(content);
  };

  const handleSaveEdit = async (messageId: string) => {
    const trimmedContent = editedContent.trim();
    if (!trimmedContent) return;

    const { activeConversationId, conversations } = useChatStore.getState();
    if (!activeConversationId) return;

    const currentConv = conversations[activeConversationId];
    if (!currentConv) return;

    const messageIndex = currentConv.messages.findIndex((msg) => msg.recordId === messageId);
    if (messageIndex === -1) return;

    const userMessage = currentConv.messages[messageIndex];
    const firstGeneration = userMessage.generations[0];
    const originalAttachments =
      firstGeneration?.attachments?.filter((att) => att.sender === 'user') || [];

    // Find the existing assistant message for this user message
    const prevAssistantMessage = currentConv.messages.find(
      (msg) => msg.role === 'assistant' && msg.userRecordId === messageId
    );

    // Reset editing state early
    setEditingMessageId(null);
    setEditedContent('');

    const selectedModel = useModelStore.getState().selectedModel;

    // Get the currently active generation's ID (the one being edited)
    const activeGeneration = userMessage.generations[userMessage.activeGenerationIndex ?? 0];
    const activeGenerationId = activeGeneration?.id || userMessage.recordId;

    try {
      // First, update the message via API - use the active generation's ID
      const response = await updateMessage(
        {
          conversationId: activeConversationId,
          messageId: activeGenerationId,
          content: trimmedContent,
          attachments: originalAttachments.map(({ fileId, fileName, extension }) => ({
            fileId,
            fileName,
            extension,
          })),
          mentions: [selectedModel?.id ?? 'auto'],
          queryMetadata: {
            isRegenerate: true,
          },
        },
        configurationSettings
      );

      const newUserGenerationId = response?.id;
      if (!newUserGenerationId) return;

      // Generate assistant placeholder ID
      const assistantGenerationId = uuidv4();

      // Update store with new user generation AND assistant placeholder
      useChatStore.setState((state) => {
        const conv = state.conversations[activeConversationId];
        if (!conv) return state;

        // Create assistant placeholder generation
        const assistantPlaceholder = {
          id: assistantGenerationId,
          content: '',
          createdDate: Date.now(),
          isThinking:
            !!selectedModel?.isThinkingModel &&
            !selectedModel?.isHarmonicModel &&
            !selectedModel?.hasDynamicThinking,
          usedModelId: selectedModel?.id,
          parentUserGenerationId: newUserGenerationId,
          isThisGenerationOnGoing: true
        };

        const updatedMessages = conv.messages.map((msg, idx) => {
          // Update user message with new generation
          if (idx === messageIndex) {
            return {
              ...msg,
              generations: [
                ...msg.generations,
                {
                  id: newUserGenerationId,
                  content: trimmedContent,
                  createdDate: Date.now(),
                  attachments: firstGeneration?.attachments,
                },
              ],
              activeGenerationIndex: msg.generations.length,
            };
          }
          // Add placeholder to existing assistant message
          if (prevAssistantMessage && msg.recordId === prevAssistantMessage.recordId) {
            return {
              ...msg,
              generations: [...msg.generations, assistantPlaceholder],
              activeGenerationIndex: msg.generations.length,
            };
          }
          return msg;
        });

        return {
          conversations: {
            ...state.conversations,
            [activeConversationId]: {
              ...conv,
              messages: updatedMessages,
              isCurrentMessageLoading: true,
              isCurrentResponseGenerating: true,
              streamingAssistantMessageId: assistantGenerationId,
            },
          },
        };
      });
    } catch (error) {
      console.error('Failed to update message:', error);
      trackEvent(EVENTS.ERROR(error, { endpoint: 'collaboration/messages', method: 'PUT' }));
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: t('FAILED_TO_UPDATE_MESSAGE'),
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditedContent('');
  };

  const copyChatBubbleIcons = [
    {
      icon: <Copy size={16} />,
      onClick: async (text: string, hasImage?: boolean, fileId?: string) => {
        if (hasImage && fileId && navigator.clipboard && window.isSecureContext) {
          await copyImageToClipboard(fileId);
        } else handleCopyToClipboard(text);
      },
      type: 'icon',
      tooltip: t('COPY'),
    },
  ];

  const messageBubbleUi = (
    message: Message,
    index: number,
    isStillGenerating: boolean,

  ) => {

    const generation = message.generations[index];
    // const checkStreamingMessage = () => {
    //   if (message.role === 'assistant') {
    //     console.log('generation in ChatMessages', generation?.isThisGenerationOnGoing)

    //   }

    // }
    // checkStreamingMessage();

    // can we use this code {"step_type": "action", "tool": "python_interpreter", "code": "import pandas as pd\nimport matplotlib.pyplot as plt\n\n# Read the Excel file into a DataFrame\ntry:\n df = pd.read_excel('/tmp/code_interpreter_rusgt571/random_people_200plus.xlsx')\nexcept Exception as e:\n print(f"Failed to read Excel file: {e}")\n # Handle the exception appropriately\n\n# Check if the DataFrame is not empty\nif not df.empty:\n # Assuming there's a column named 'values' or a similar numeric column\n numeric_columns = df.select_dtypes(include=['int64', 'float64']).columns\n \n if len(numeric_columns) > 0:\n # Use the first numeric column found for analysis\n value_column = numeric_columns[0]\n \n # Get the top 10 values\n top_10 = df.nlargest(10, value_column)\n \n # Create a bar chart\n plt.figure(figsize=(10,6))\n plt.bar(top_10.index, top_10[value_column])\n plt.xlabel('Index')\n plt.ylabel(value_column)\n plt.title(f'Top 10 {value_column}')\n plt.xticks(rotation=90) # Rotate x-axis labels for better readability\n \n # Save the plot\n plt.savefig('/tmp/code_artifacts_cchg7pnw/top_10_values_plot.png')\n plt.close()\n \n print("Top 10 values and plot created successfully.")\n else:\n print("No numeric columns found in the data.")\nelse:\n print("The DataFrame is empty.")", "observations": "Execution logs:\nTop 10 values and plot created successfully.\nLast output from code snippet:\n(array([-20., 0., 20., 40., 60., 80., 100.]), [Text(-20.0, 0, '\u221220'), Text(0.0, 0, '0'), Text(20.0, 0, '20'), Text(40.0, 0, '40'), Text(60.0, 0, '60'), Text(80.0, 0, '80'), Text(100.0, 0, '100')])"}}
    // in react markdown

    if (generation.searchSources) {
      generation.content = placeHrefInContent(generation.content, generation.searchSources);
      generation.searchSources = generation.searchSources.filter((source) => {
        return source.metadata?.url && isValidUrl(source.metadata.url);
      });
    }

    // Add code execution steps to content if they exist

    const assistantAttachments = generation.attachments?.filter(
      (attachment) => attachment.sender === 'assistant'
    );
    // console.log('assistantAttachments in ChatMessages', assistantAttachments)
    const assistantGeneratedImages = generation.generatedImages?.filter(
      (attachment) => attachment.sender === 'assistant'
    );

    const userAttachments = generation.attachments?.filter(
      (attachment) => attachment.sender === 'user'
    );
    const sources = generation?.sources || [];

    const handleSendMessage = (message: string, files: any[]) => {
      onSendMessage?.(message, files);
    };
    // console.log('generation', generation)

    const isEditing = editingMessageId === `${message.recordId}-${generation.id}`;

    return (
      <div
        className={`${message.role === 'user' ? (isEditing ? 'w-full' : 'min-w-full') : 'w-full flex flex-col'} mb-1`}
      >
        <div
          className={`flex items-center min-w-0 ${message.role === 'user' && isEditing ? 'justify-start' : message.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div
            className={`${isEditing ? 'w-full relative rounded-2xl border shadow-md p-4' : `${message.role == 'user' ? (theme === 'dark' ? 'bg-[#27272c]' : 'bg-primary-light') : ''} w-full px-4 py-2 rounded-[8px]`} ${isEditing ? (theme === 'dark' ? 'bg-[#272727] border-gray-600' : 'bg-white border-gray-200') : ''}`}
          >
            <div className="break-words min-w-0">
              {message.role === 'user' && userAttachments && userAttachments.length > 0 && (
                <div className="flex flex-col items-end justify-end gap-2 mb-4">
                  {userAttachments.map((file) => {
                    if (
                      modelSupportedExtensionsMap[ModelTypes.VISION].image.includes(
                        `.${file.extension}`
                      )
                    ) {
                      return <ImagePreview key={file.fileId} itemId={file.fileId} />;
                    }

                    return (
                      <div
                        key={file.fileId}
                        className={`flex items-center justify-between p-2 rounded mt-2 ${theme === 'dark' ? 'bg-[#27272c]' : 'bg-primary-light'}`}
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className={`text-sm ${theme === 'dark' ? 'text-primary-200' : 'text-neutral-800'} truncate max-w-[180px]`}>
                            {file.fileName}
                          </span>
                        </div>
                        {file.sender === 'assistant' &&
                          file.status &&
                          file.status === FileGenerationStatus.IN_PROGRESS && (
                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          )}
                      </div>
                    );
                  })}
                </div>
              )}
              {generation.searchSources && generation.searchSources.length > 0 && (
                <SearchSources sources={generation.searchSources} />
              )}
              {generation.thinkContent && (
                <ExpandableThinkContainer
                  content={generation.thinkContent ?? ''}
                  isThinking={generation.isThinking}
                  className="mb-2"
                />
              )}
              {generation.isAnalyzing && !(generation.thinkContent && !generation.content) && (
                <AnalyzingIndicator isAnalyzingStep={false} isAnalyzing={true} />
              )}
              {generation.isAnalyzed && (
                <AnalyzingIndicator
                  isAnalyzingStep={true}
                  isAnalyzing={false}
                  codeExecutionSteps={(generation as any).codeExecutionSteps}
                />
              )}
              {/* {generation.content && message.toolsStatus && message.toolsStatus[Tools.SEARCH] && (
            <span className="inline-block italic bg-[#e9e9e980] px-2 py-1 rounded my-1">
              {t('SEARCH_RESULT')}:{' '}
            </span>
          )} */}
              {message.role === 'user' && isEditing ? (
                <div className="relative w-full">
                  <Textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="resize-none shadow-none border-none focus-visible:shadow-none focus-visible:border-none focus-visible:ring-0 min-h-[48px] px-3 py-1 pr-20 rounded-md outline-none whitespace-pre-wrap break-words overflow-hidden"
                    style={{
                      fontFamily: 'inherit',
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      color: theme === 'dark' ? '#e5e5e5' : '#1a1a1a',
                      minHeight: '80px',
                      maxHeight: '160px',
                      backgroundColor: 'transparent',
                    }}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        handleSaveEdit(`${message.recordId}`);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        handleCancelEdit();
                      }
                    }}
                  />
                  <div className="absolute bottom-2 right-2 flex gap-2 items-center">
                    <Button
                      variant="outline"
                      aria-label="Cancel"
                      className="disabled:opacity-40 flex items-center justify-center h-8 hover:bg-muted rounded-full border w-8"
                      onClick={handleCancelEdit}
                    >
                      <X size={16} />
                    </Button>
                    <Button
                      variant="outline"
                      disabled={!editedContent.trim()}
                      aria-disabled="false"
                      aria-label="Send message"
                      className="disabled:opacity-40 flex items-center justify-center h-8 hover:bg-muted rounded-full border w-8"
                      onClick={() => handleSaveEdit(`${message.recordId}`)}
                    >
                      <Send size={16} />
                    </Button>
                  </div>
                </div>
              ) : (
                <StreamingContent
                  content={generation.content}
                  isUserMessage={message.role === 'user'}
                />
              )}
            </div>
          </div>
        </div>

        <>
          {message.role === 'assistant' &&
            assistantAttachments &&
            assistantAttachments.length > 0 && (
              <div className="flex flex-wrap justify-start gap-2 mb-2">
                {assistantAttachments.map((file) => (
                  <AssistantAttachmentPreview key={file.fileId} file={file} />
                ))}
              </div>
            )}
        </>
        <>
          {message.role === 'assistant' &&
            assistantGeneratedImages &&
            assistantGeneratedImages.length > 0 && (
              <div className="flex flex-wrap justify-start gap-2 mb-2">
                {assistantGeneratedImages.map((file) => (
                  <AssistantAttachmentPreview key={file.fileId} file={file} />
                ))}
              </div>
            )}
        </>
        <>
          {message.role === 'assistant' && sources && sources.length > 0 && (
            <div className="flex items-start justify-start gap-2 mb-2 ml-4">
              <p className="text-[10px] font-semibold">{t('SOURCES')}:</p>
              <div className="flex flex-wrap justify-start gap-2 mb-2">
                {sources.map((source) => (
                  <SourceFile key={source.fileId} sourceFile={source} />
                ))}
              </div>
            </div>
          )}
        </>
        <div>
          {message.role == 'assistant' && !generation?.isThisGenerationOnGoing && (
            <>
              <div className="flex  md:gap-2 lg:gap-2  justify-start md:pl-3 lg:pl-3">
                {message.generations.length > 1 && (
                  <ChangeMessage
                    recordId={message.recordId}
                    currentMessageIndex={message.activeGenerationIndex ?? 0}
                    totalMessageCount={message.generations.length}
                  />
                )}
                {copyChatBubbleIcons.map((icon) => (
                  <button
                    title={icon.tooltip}
                    key={icon.type}
                    className="flex items-center justify-center h-8 w-8 p-1.5 rounded-full hover:bg-muted"
                    onClick={async () =>
                      await icon.onClick(
                        generation.content,
                        (generation.generatedImages ?? []).length > 0,
                        (generation.generatedImages ?? []).length > 0
                          ? (generation.generatedImages ?? [])[0].fileId
                          : undefined
                      )
                    }
                  >
                    {icon.icon}
                  </button>
                ))}

                <Feedback
                  recordId={message.userRecordId ?? ''}
                  message={generation}
                  isStillGenerating={isStillGenerating}
                  hasParticipants={false}
                  hideRegenerateButton={
                    (assistantAttachments ?? []).length > 0 ||
                    (generation.HasFileGeneratedResponse ?? false) ||
                    (assistantGeneratedImages ?? []).length > 0
                  }
                />
              </div>
            </>
          )}
          {message.role == 'user' && !isEditing && (
            <div className="flex gap-1 justify-end">
              {message.generations.length > 1 && (
                <ChangeMessage
                  recordId={message.recordId}
                  currentMessageIndex={message.activeGenerationIndex ?? 0}
                  totalMessageCount={message.generations.length}
                />
              )}
              {copyChatBubbleIcons.map((icon) => (
                <button
                  title={icon.tooltip}
                  key={icon.type}
                  className="flex items-center justify-center h-8 w-8 p-1.5 rounded-full hover:bg-muted"
                  onClick={() => icon.onClick(generation.content)}
                >
                  {icon.icon}
                </button>
              ))}
              {(!userAttachments || userAttachments.length === 0) && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="flex items-center justify-center h-8 w-8 p-1.5 rounded-full hover:bg-muted"
                  disabled={isStillGenerating}
                  onClick={() =>
                    !isStillGenerating &&
                    handleEditMessage(`${message.recordId}-${generation.id}`, generation.content)
                  }
                  title={t('EDIT') || 'Edit'}
                >
                  <Pencil className="w-3 h-3" />
                </Button>
              )}
            </div>
          )}
        </div>
        {message.role === 'assistant' && (
          <SearchRelatedImageList
            isSkeletonShowForImages={generation.isSkeletonShowForImages}
            messageId={message.recordId}
            imageList={generation.relatedSearcheImageList}
          />
        )}
        {message.role === 'assistant' && (
          <SearchRelatedVideoList
            isSkeletonShowForVideos={generation.isSkeletonShowForVideos}
            messageId={message.recordId}
            videoList={generation.relatedSearcheVideoList}
          />
        )}
        {message.role === 'assistant' && (
          <SearchRelatedList
            isSkeletonShowForQuestions={generation.isSkeletonShowForQuestions}
            messageId={message.recordId}
            onSendMessage={handleSendMessage}
            questionList={generation.relatedSearcheQuestionsList}
          />
        )}
      </div>
    );
  };

  return (
    <div
      ref={messageContainerRef}
      className="w-full overflow-y-auto flex justify-center"
      style={{ maxHeight: `calc(100vh - ${chatInputHeight}px - 83px)` }}
    >
      <div className="flex flex-col gap-1.5 w-[95%] md:w-[75%] lg:w-[65%] mb-[130px]">
        {messages.map((message, index) => {
          const stableRecordId = message.recordId || message.generations?.[0]?.id || String(index);
          const messageKey = `${message.role}-${stableRecordId}`;

          const userActiveGenIndex = message.activeGenerationIndex ?? 0;
          const userActiveGenId = message.generations[userActiveGenIndex]?.id;

          return message.role === 'user' ? (
            <div
              key={messageKey}
              className={`flex ${editingMessageId === `${message.recordId}-${userActiveGenId}` ? 'justify-start' : 'justify-end'} items-start gap-0.5 mb-2`}
            >
              <div
                className={
                  editingMessageId === `${message.recordId}-${userActiveGenId}`
                    ? 'w-full'
                    : 'max-w-[90%] relative'
                }
              >
                {messageBubbleUi(message, userActiveGenIndex, isStillGenerating)}
              </div>
            </div>
          ) : streamingMessage &&
            streamingMessage.userRecordId === message.userRecordId &&
            isLoading &&
            !message.generations[message.activeGenerationIndex ?? 0].isAnalyzing &&
            !message.generations[message.activeGenerationIndex ?? 0].isAnalyzed ? (
            <MessageLoading key={`${messageKey}-loading`} />
          ) : streamingMessage &&
            streamingMessage.userRecordId === message.userRecordId &&
            isStillGenerating ? (
            <div key={messageKey} className="flex justify-start items-start gap-1">
              <img
                className="w-7 h-7 mt-[8px] border-[1px] p-1 rounded-full outline-gray-200 bg-primary-50"
                src={logo}
                alt="bebrave logo"
              />

              <div className="max-w-[90%] relative">
                {messageBubbleUi(
                  message,
                  message.activeGenerationIndex ?? 0,
                  isStillGenerating,

                )}
              </div>
            </div>
          ) : (
            message.role === 'assistant' && (
              <div key={messageKey} className="flex justify-start items-start gap-1">
                <img
                  className="w-7 h-7 mt-[8px] border-[1px] p-1 rounded-full outline-gray-200 bg-primary-50"
                  src={logo}
                  alt="bebrave logo"
                />

                <div className="max-w-[90%] relative">
                  {messageBubbleUi(
                    message,
                    message.activeGenerationIndex ?? 0,
                    isStillGenerating,

                  )}
                </div>
              </div>
            )
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      <div
        className="flex justify-center absolute w-[85%] md:w-[50%] lg:w-[50%]"
        style={{ bottom: `${chatInputHeight}px` }}
      >
        <Button
          ref={scrollToBottomButton}
          variant="outline"
          aria-label="Scroll to bottom"
          className="h-10 rounded-full border w-10"
          onClick={scrollToBottom}
        >
          <ArrowDown size={16} />
        </Button>
      </div>
    </div>
  );
};


